import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import {
  LibraryAdd as LibraryAddIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { ExerciseBankPickerDialog } from '@/components/exercises/ExerciseBankPickerDialog'
import { ExerciseCheckForm } from '@/components/exercises/ExerciseCheckForm'
import { exerciseRunsApi, exercisesApi, type Exercise, type ExerciseRun } from '@/api/exercises'
import type { AssignedThemeElement } from '@/api/programs'
import { ROUTES } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'

/**
 * Задания темы на уроке: выдача в класс и живые плитки «кто на каком проценте».
 *
 * Учителю — список заданий темы и состояние попыток; ученику — кнопка «решать»
 * для выданного ему задания. Сводка обновляется по событию урока, а не по
 * таймеру: класс из пятнадцати человек опрашивал бы сервер пятнадцать раз в
 * секунду ни за чем.
 *
 * Кроме заданий тем панель показывает работы, выданные мимо программы: сервер
 * разрешает выдать на урок любое задание банка (слот назначения у такой работы
 * пустой), и она приходит в списке работ урока, но в списке заданий тем её нет
 * и никогда не будет. Поэтому такие работы рисуются по вложенному в попытку
 * заданию, а не по списку тем.
 */

type Props = {
  lessonId: number
  /** Элементы темы урока: задания живут в элементах */
  elements: AssignedThemeElement[]
  isTeacher: boolean
  /** Меняется при realtime-событии задания на канале урока */
  refreshToken?: number
}

/** Работа сдана: у делегата состояние живёт в своём домене, а не в попытке-ссылке */
const isDone = (run: ExerciseRun): boolean =>
  run.delegate ? run.delegate.status !== 'active' : run.status !== 'active'

/**
 * Замок на секцию «выдано вне программы».
 *
 * Панель рисуется под КАЖДОЙ темой урока, а работа, выданная мимо программы, ни
 * к одной из тем не относится. Без замка одна и та же попытка висела бы под
 * каждой темой урока, и кнопок отмены у неё было бы столько же, сколько тем.
 * Секцию и кнопку выдачи держит панель, смонтированная первой; когда она
 * уходит, остальные узнают об этом по подписке и подхватывают слот.
 */
const offProgramOwners = new Map<number, object>()
const offProgramWatchers = new Map<number, Set<() => void>>()

function useOffProgramSlot(lessonId: number): boolean {
  const [owns, setOwns] = useState(false)

  useEffect(() => {
    const token = {}

    const claim = () => {
      if (!offProgramOwners.has(lessonId)) offProgramOwners.set(lessonId, token)

      setOwns(offProgramOwners.get(lessonId) === token)
    }

    const watchers = offProgramWatchers.get(lessonId) ?? new Set<() => void>()
    watchers.add(claim)
    offProgramWatchers.set(lessonId, watchers)
    claim()

    return () => {
      watchers.delete(claim)
      if (watchers.size === 0) offProgramWatchers.delete(lessonId)

      if (offProgramOwners.get(lessonId) === token) {
        offProgramOwners.delete(lessonId)
        // Слот освободился — его подхватит любая оставшаяся панель урока
        watchers.forEach((watcher) => watcher())
      }
    }
  }, [lessonId])

  return owns
}

type TileProps = {
  exercise: Exercise
  /** Попытки по этому заданию на уроке, без снятых */
  runs: ExerciseRun[]
  isTeacher: boolean
  /** Запрос по этому заданию в полёте — спиннер рисуем на его кнопке */
  busy: boolean
  /** Любой запрос панели в полёте: нажать второй раз нельзя ни по какой кнопке */
  locked: boolean
  onLaunch: (exercise: Exercise) => void
  onCancel: (exercise: Exercise, active: ExerciseRun[]) => void
  onSolve: (exercise: Exercise, mine: ExerciseRun | null) => void
  onCheck: (runId: number) => void
}

/**
 * Плитка одного задания: кнопка действия и состояние попыток класса.
 *
 * Вынесена из панели, потому что рисуется дважды — для заданий темы и для
 * работ, выданных мимо программы. Копия вёрстки разъезжалась бы уже на первой
 * правке.
 */
function ExerciseTile({
  exercise,
  runs,
  isTeacher,
  busy,
  locked,
  onLaunch,
  onCancel,
  onSolve,
  onCheck,
}: TileProps) {
  const { t } = useTranslation()

  const active = runs.filter((run) => !isDone(run))
  const done = runs.filter(isDone)

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2">{exercise.title}</Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={t(`exercises.types.${exercise.type}.name`)} />
            {exercise.status !== 'ready' && (
              <Chip size="small" color="warning" label={t('exercises.exerciseStatus.draft')} />
            )}
          </Stack>
        </Box>

        {isTeacher ? (
          <Button
            variant="contained"
            size="small"
            // Спиннер вместо иконки: заблокированная кнопка без него читается
            // как сломанная, и учитель бьёт по ней повторно
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
            disabled={locked || exercise.status !== 'ready'}
            onClick={() => onLaunch(exercise)}
          >
            {runs.length > 0 ? t('exercises.lesson.relaunch') : t('exercises.lesson.launch')}
          </Button>
        ) : (
          <Button
            variant={active.length > 0 ? 'contained' : 'outlined'}
            size="small"
            startIcon={busy ? <CircularProgress size={16} color="inherit" /> : undefined}
            disabled={locked || exercise.status !== 'ready'}
            // Выданная учителем попытка важнее самопрактики: её результат идёт
            // в зачёт темы, тренировочная — нет.
            onClick={() => onSolve(exercise, active[0] ?? done[0] ?? null)}
          >
            {active.length > 0 ? t('exercises.lesson.solve') : t('exercises.lesson.practice')}
          </Button>
        )}
      </Stack>

      {isTeacher && runs.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={(done.length / runs.length) * 100}
            sx={{ mb: 1, borderRadius: 1 }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {runs.map((run) => (
              <Chip
                key={run.id}
                size="small"
                label={`${run.student?.name ?? `#${run.student_id}`}${
                  run.score_percent === null ? '' : ` · ${run.score_percent}%`
                }`}
                color={
                  run.status === 'checked'
                    ? 'success'
                    : run.status === 'teacher_review'
                      ? 'warning'
                      : 'default'
                }
                variant={run.status === 'active' ? 'outlined' : 'filled'}
                onClick={() => {
                  // Нерешённую работу проверять нечего: открываем только
                  // сданные. Проверка идёт прямо здесь — уходить с урока ради
                  // одной оценки незачем
                  if (isDone(run)) onCheck(run.id)
                }}
              />
            ))}
          </Stack>
          {active.length > 0 && (
            <Button
              size="small"
              startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
              sx={{ mt: 1 }}
              disabled={locked}
              onClick={() => onCancel(exercise, active)}
            >
              {t('exercises.lesson.cancelActive')}
            </Button>
          )}
        </Box>
      )}
    </Paper>
  )
}

export function LessonExercisePanel({ lessonId, elements, isTeacher, refreshToken = 0 }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [exercises, setExercises] = useState<Exercise[]>([])
  const [runs, setRuns] = useState<ExerciseRun[]>([])
  /** Задания ВСЕХ тем урока: по ним отличаем работу, выданную мимо программы */
  const [programExerciseIds, setProgramExerciseIds] = useState<Set<number>>(new Set())
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Попытка, открытая на проверку в модалке урока
  const [checkingRunId, setCheckingRunId] = useState<number | null>(null)

  // Двойник busyId в ссылке: обработчик кнопки читает состояние из замыкания
  // своего рендера, и два быстрых клика успевали пройти оба до перерисовки —
  // класс получал две выдачи одного задания.
  const busyRef = useRef(false)

  const ownsOffProgramSlot = useOffProgramSlot(lessonId)

  // Состав заданий темы строкой: prop-массив приходит новым на каждый рендер
  // родителя, и зависимость от него самого перезапрашивала бы список на каждое
  // изменение состояния урока
  const ownExerciseIdsKey = useMemo(
    () =>
      elements
        .flatMap((element) => (element.type === 'exercise' && element.exercise_id != null ? [element.exercise_id] : []))
        .sort((a, b) => a - b)
        .join(','),
    [elements],
  )

  const ownExerciseIds = useMemo(
    () => new Set(ownExerciseIdsKey === '' ? [] : ownExerciseIdsKey.split(',').map(Number)),
    [ownExerciseIdsKey],
  )

  const hasExercises = ownExerciseIds.size > 0
  // Тема без заданий не должна дёргать API вовсе. Исключение — панель со слотом
  // «вне программы»: работы урока ей нужны, даже когда в теме нет ни одного
  // задания.
  const needsData = hasExercises || ownsOffProgramSlot

  const load = useCallback(async (background = false) => {
    if (!needsData) {
      setExercises([])
      setRuns([])
      setProgramExerciseIds(new Set())
      setLoading(false)

      return
    }

    // Обновление по событию урока идёт фоном: иначе на каждую сдачу любого
    // ученика весь блок мигал бы скелетоном.
    if (!background) setLoading(true)
    setError(null)
    try {
      // Один запрос на всю тему вместо запроса на каждое задание: у класса из
      // пятнадцати человек прежний вариант давал пятнадцать вееров запросов, а
      // ученику ещё и 403 — карточка задания учительская.
      const [loaded, lessonRuns] = await Promise.all([
        exercisesApi.listByLesson(lessonId),
        // Ученику приходят только его попытки — список тот же, фильтрует сервер
        exerciseRunsApi.listByLesson(lessonId),
      ])
      // Отбор по заданиям СВОЕЙ темы: эндпоинт отдаёт задания всего урока, а
      // при нескольких темах панель рисуется под каждой. Фильтруем по ссылкам
      // элементов темы — задание банка стоит в нескольких местах, и признака
      // принадлежности у него самого больше нет
      setExercises(loaded.filter((exercise) => ownExerciseIds.has(exercise.id)))
      // Полный состав программы урока запоминаем отдельно: работа по заданию
      // соседней темы не должна попасть в секцию «вне программы»
      setProgramExerciseIds(new Set(loaded.map((exercise) => exercise.id)))
      setRuns(lessonRuns)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setLoading(false)
    }
  }, [needsData, lessonId, ownExerciseIds, t])

  const initialisedRef = useRef(false)

  useEffect(() => {
    void load(initialisedRef.current)
    initialisedRef.current = true
  }, [load, refreshToken])

  /**
   * Работы урока, выданные мимо программы, — по вложенному в попытку заданию.
   *
   * Ждать такое задание в списке заданий тем бессмысленно: оно не стоит ни в
   * одной теме урока и в тот список не попадёт никогда. Признак — задание вне
   * состава программы урока, а не пустой слот назначения: слот бывает пустым и
   * у работы по теме, если у ученика нет назначения.
   */
  const offProgram = useMemo(() => {
    if (!ownsOffProgramSlot) return []

    const groups = new Map<number, { exercise: Exercise; runs: ExerciseRun[] }>()

    for (const run of runs) {
      if (run.context !== 'lesson' || run.status === 'cancelled') continue
      if (programExerciseIds.has(run.exercise_id)) continue

      const exercise = run.exercise
      // Без вложенного задания плитку нечем подписать: пропускаем, а не рисуем
      // строку «#17» — она учителю ничего не говорит
      if (!exercise) continue

      const group = groups.get(run.exercise_id) ?? { exercise, runs: [] }
      group.runs.push(run)
      groups.set(run.exercise_id, group)
    }

    return [...groups.values()]
  }, [ownsOffProgramSlot, runs, programExerciseIds])

  const launch = async (exercise: Exercise) => {
    if (busyRef.current) return

    busyRef.current = true
    setBusyId(exercise.id)
    setError(null)
    try {
      await exerciseRunsApi.launch(lessonId, exercise.id)
      setRuns(await exerciseRunsApi.listByLesson(lessonId))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      busyRef.current = false
      setBusyId(null)
    }
  }

  /**
   * Выдача задания из банка: сервер не требует, чтобы задание стояло в теме, —
   * попытка заводится с пустым слотом назначения. В списке заданий тем такого
   * задания не появится, плитку по нему панель соберёт из самой попытки.
   *
   * Ошибку сюда не глотаем: её показывает диалог выбора и остаётся открытым,
   * чтобы учитель тут же взял другое задание.
   */
  const launchFromBank = async (exercise: Exercise) => {
    await exerciseRunsApi.launch(lessonId, exercise.id)
    setPickerOpen(false)
    void load(true)
  }

  const cancelRuns = async (exercise: Exercise, targets: ExerciseRun[]) => {
    if (busyRef.current) return

    busyRef.current = true
    setBusyId(exercise.id)
    setError(null)
    try {
      // Список перезапрашиваем один раз в конце: по запросу на каждую отмену
      // давало пятнадцать лишних выборок на классе из пятнадцати человек.
      await Promise.all(targets.map((run) => exerciseRunsApi.cancel(run.id)))
      setRuns(await exerciseRunsApi.listByLesson(lessonId))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      busyRef.current = false
      setBusyId(null)
    }
  }

  const solve = async (exercise: Exercise, mine: ExerciseRun | null) => {
    if (mine) {
      navigate(ROUTES.EXERCISE_RUN(mine.id))

      return
    }

    if (busyRef.current) return

    busyRef.current = true
    setBusyId(exercise.id)
    setError(null)
    try {
      const run = await exerciseRunsApi.selfRun(exercise.id)
      navigate(ROUTES.EXERCISE_RUN(run.id))
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      busyRef.current = false
      setBusyId(null)
    }
  }

  // Панель без заданий темы нужна только держателю слота: у него живут кнопка
  // выдачи и работы вне программы. Ученику пустая панель не нужна вовсе
  if (!hasExercises && !ownsOffProgramSlot) return null
  if (!hasExercises && !isTeacher && offProgram.length === 0) return null

  const showLauncher = isTeacher && ownsOffProgramSlot

  return (
    <Box sx={{ mt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle2">{t('exercises.lesson.title')}</Typography>
        {/* Выдать можно ЛЮБОЕ задание банка, не только стоящее в теме: на уроке
            тема уходит в сторону чаще, чем успевает измениться программа */}
        {showLauncher && (
          <Button
            size="small"
            startIcon={<LibraryAddIcon />}
            sx={{ ml: 'auto' }}
            onClick={() => setPickerOpen(true)}
          >
            {t('exercises.lesson.launchFromBank', 'Выдать задание')}
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && <Skeleton variant="rounded" height={96} />}

      {!loading && (
        <>
          {hasExercises ? (
            <Stack spacing={2}>
              {exercises.map((exercise) => (
                <ExerciseTile
                  key={exercise.id}
                  exercise={exercise}
                  runs={runs.filter((run) => run.exercise_id === exercise.id && run.status !== 'cancelled')}
                  isTeacher={isTeacher}
                  busy={busyId === exercise.id}
                  locked={busyId !== null}
                  onLaunch={(target) => void launch(target)}
                  onCancel={(target, active) => void cancelRuns(target, active)}
                  onSolve={(target, mine) => void solve(target, mine)}
                  onCheck={setCheckingRunId}
                />
              ))}
            </Stack>
          ) : (
            showLauncher && (
              <Typography variant="body2" color="text.secondary">
                {t('exercises.lesson.emptyTheme', 'В теме заданий нет — можно выдать любое из банка')}
              </Typography>
            )
          )}

          {/* Работы вне программы — отдельной секцией: они не относятся к теме,
              и мешать их с её заданиями значит врать про состав темы */}
          {offProgram.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2">
                {t('exercises.lesson.offProgramTitle', 'Выдано вне программы')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t(
                  'homeworks.exercisePicker.bankHint',
                  'Задание вне программы ученика — в статистику темы оно не войдёт',
                )}
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {offProgram.map((group) => (
                  <ExerciseTile
                    key={group.exercise.id}
                    exercise={group.exercise}
                    runs={group.runs}
                    isTeacher={isTeacher}
                    busy={busyId === group.exercise.id}
                    locked={busyId !== null}
                    onLaunch={(target) => void launch(target)}
                    onCancel={(target, active) => void cancelRuns(target, active)}
                    onSolve={(target, mine) => void solve(target, mine)}
                    onCheck={setCheckingRunId}
                  />
                ))}
              </Stack>
            </Box>
          )}
        </>
      )}

      <ExerciseBankPickerDialog
        open={pickerOpen}
        title={t('exercises.lesson.pickerTitle', 'Выдать задание на уроке')}
        hint={t(
          'homeworks.exercisePicker.bankHint',
          'Задание вне программы ученика — в статистику темы оно не войдёт',
        )}
        // Черновик выдать нельзя: сервер отвечает 422, и строка в банке должна
        // быть погашена ещё до нажатия
        requireReady
        onPick={launchFromBank}
        onClose={() => setPickerOpen(false)}
      />

      <Dialog
        open={checkingRunId !== null}
        // Промах мимо модалки не должен стирать набранный комментарий:
        // закрывается только кнопкой
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') setCheckingRunId(null)
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('exercises.check.title')}</DialogTitle>
        <DialogContent dividers>
          {checkingRunId !== null && (
            <ExerciseCheckForm
              runId={checkingRunId}
              onChecked={() => {
                setCheckingRunId(null)
                // Плитки показывают статус и балл — после проверки они устарели
                void load(true)
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCheckingRunId(null)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
